import { Card, Form, Input, message } from '@nackle/paper';
import { navigate } from '@reach/router';
import { ROM_PATH_CREATE, ROM_PATH_UPDATE } from 'app/components/routes/paths';
import { ValidationMapOf } from 'app/components/shared/helper-text-helpers';
import ImageUpload from 'components/shared/image-upload';
import isEmpty from 'lodash/isEmpty';
import React, { Component, FormEvent, ReactNode } from 'react';
import { CardHeader } from 'shared/card-header';
import { HasPermission } from 'shared/HasPermission';
import RomUpload from 'shared/rom-upload';
import SaveButton from 'shared/save-button';
import { defined } from 'utils/defined';
import { AnyFormElement } from 'utils/viewtypes';
import { clearRom, getRom, setRomValue } from './actions';
import { RomEditorState } from './interfaces';
import { validateRom } from './validation';
import '@nackle/game-loader-component/static/loader.css';

interface RomFormProps {
    readonly rom: RomEditorState;
    readonly romId?: string;
    readonly path?: string;
    readonly sessionId: string;
    readonly setRomValue: typeof setRomValue;
    readonly createRom: (rom: RomEditorState) => Promise<any>;
    readonly updateRom: (rom: RomEditorState, romId: string) => Promise<any>;
    readonly getRom: typeof getRom;
    readonly clearRom: typeof clearRom;
    readonly playRom: (romId: string) => void;
}

interface State {
    playingRomId: string | null;
    validationErrors: ValidationMapOf<RomEditorState>;
}

export default class RomForm extends Component<RomFormProps> {

    public state: State = {
        playingRomId: null,
        validationErrors: {},
    };

    constructor(props: RomFormProps) {
        super(props);
        if (props.path === ROM_PATH_UPDATE) {
            props.getRom(props.romId);
        }
    }

    componentDidUpdate(prevProps: RomFormProps) {
        const { rom, path } = this.props;
        const isCreatePath = path === ROM_PATH_CREATE;
        if (isCreatePath && !prevProps.romId && !!rom.romId) {
            navigate(`./${rom.romId}/update`);
        }
    }

    setValue = async <K extends keyof RomEditorState>(name: K, value: RomEditorState[K]) => {
        await this.props.setRomValue(name, value);
        this.clearValidationError(name);
    };

    clearValidationError = (name: string) => {
        this.setState((state: State) => {
            const validationErrors = { ...state.validationErrors };
            delete validationErrors[name];
            return { validationErrors };
        });
    };

    handleValue = (event: FormEvent<AnyFormElement>) => {
        const { name, value } = event.target as AnyFormElement;
        this.setValue(name as keyof RomEditorState, value);
    };

    handleImage = (image: string | null) => this.setValue('image', image);

    handleBannerImage = (image: string | null) => this.setValue('bannerImage', image);

    handleChangeMetaData = (metaData: string) => this.setValue('metaData', metaData);

    removeImage = () => this.setValue('image', null);

    removeBannerImage = () =>this.setValue('bannerImage', null);

    handleRomFile = (romFile: Blob | null, romFileName: string | null) => {
        this.setValue('gameFile', romFile || undefined);
        this.setValue('gameFileName', romFileName || undefined);
    };

    removeRomFile = () => {
        this.setValue('gameFile', undefined);
        this.setValue('gameFileName', undefined);
        this.setValue('resource', undefined);
    };

    saveRom = async () => {
        const { rom, romId, path } = this.props;

        const mode = (path === ROM_PATH_UPDATE) ? 'update' : 'create';
        const validationErrors = validateRom(rom, mode);
        this.setState({ validationErrors });
        if (!isEmpty(validationErrors)) {
            message.error({
                content: 'One or more fields are invalid',
            });
            throw new Error('One or more fields are invalid');
        }
            switch (path) {
                case ROM_PATH_UPDATE:
                    return this.props.updateRom(rom, defined(romId));
                case ROM_PATH_CREATE:
                default:
                    return this.props.createRom(rom);
            }
    };

    playRomFile = () => this.props.playRom(defined(this.props.romId));

    public componentWillUnmount() {
        this.props.clearRom();
    }

    public render() {
        const {
            path,
            rom: {
                name,
                description,
                resource,
                gameFile,
                image,
                bannerImage,
            },
        } = this.props;
        const {
            validationErrors,
        } = this.state;

        let saveButton: ReactNode;
        if (path === ROM_PATH_UPDATE) {
            saveButton = (
                <HasPermission app="arcade-api" permission="edit-rom">
                    {(hasPermission: boolean) =>
                        <SaveButton
                            id="saveRomButton"
                            disabled={!hasPermission}
                            save={this.saveRom}
                        />
                    }
                </HasPermission>
            );
        } else {
            saveButton = (
                <SaveButton
                    id="saveRomButton"
                    save={this.saveRom}
                />
            );
        }

        return (
            <div className="content">
                <Card>
                    <CardHeader
                        title="ROM Configuration"
                        right={saveButton}
                        className="rom-card-header"
                    />

                    <Form style={{marginBottom: 20}}>
                    <Form.Item
                        label="Game ROM Name"
                        {...validationErrors.name}
                    />
                        <Input
                            defaultValue="Name to appear in Arcade Studio"
                            name="name"
                            value={ name }
                            type="text"
                            onChange={this.handleValue}
                        />
                    </Form>
                    <Form style={{marginBottom: 20}}>
                    <Form.Item
                        label="Description"
                    />
                        <Input.TextArea
                            placeholder="Description to appear as a fallback if no translations are configured"
                            autoSize={true}
                            rows={5}
                            name="description"
                            onChange={this.handleValue}
                            value={ description }
                            style={{paddingBottom: 100}}
                        />
                    </Form>
                    <Form style={{marginBottom: 20}}>
                    <Form.Item
                        label="Upload Game ROM .zip File"
                        {...validationErrors.gameFile}
                    />
                        <RomUpload
                            romFile={gameFile}
                            resource={resource}
                            onPlayRomFile={this.playRomFile}
                            onRemove={this.removeRomFile}
                            handleRomFile={this.handleRomFile}
                        />
                    </Form>
                    <div className="row">
                        <div className="col-sm-6">
                            <Form.Item
                                label="Thumbnail Image"
                                className="thumbnail-image"
                                >
                                <Form.Item>
                                <ImageUpload
                                    imageUrl={image}
                                    handleImage={this.handleImage}
                                    removeImage={this.removeImage}
                                />
                                </Form.Item>
                                </Form.Item>
                        </div>
                        <div className="col-sm-6">
                            <Form.Item
                                label="Banner Image"
                                className="banner-image"
                            >
                                <ImageUpload
                                    imageUrl={bannerImage}
                                    handleImage={this.handleBannerImage}
                                    removeImage={this.removeBannerImage}
                                />
                                </Form.Item>
                        </div>
                    </div>
                        {/* TODO: There's metaData and metaDataValues. I'm not sure what the two of them mean, but
                        TODO: metaDataValues only appears on the Cabinet, and it's the only one that's used in
                        TODO: the game loader. Need to figure out what metaData is supposed to be used for. */}
                </Card>
            </div>
        );
    }
}
